import React, { useState, useEffect, useRef } from 'react'
import { Dialog, TextInput, Label, Button, Text, Flex, Selector, DateTimePickerV2 } from '@leverege/ui-elements'

import S from './MaintenanceEventEditorStyle.css'

const initialState = {
  type : { value : 'downtime', error : null },
  description : { value : '', error : null },
  startTime : { value : null, error : null },
  endTime : { value : null, error : null },
}

const options = [
  { name : 'Downtime', value : 'downtime' },
  { name : 'Degradation', value : 'degradation' }
]

/**
 * Dialog for creating a new enum type
 * @param {boolean} show does the dialog show
 * @param {function} onClose
 * @param {function} onSubmit
 * @param {any} eventData returned with onSubmit and onClose
 */
export default function MaintenanceEventCreateDialog( { show, onClose, eventData, onSubmit } ) {
  const [ formData, setFormData ] = useState( initialState )

  const dataRef = useRef( formData )
  useEffect( () => { dataRef.current = formData }, [ formData ] )

  const handleSubmit = () => {
    const value = Object.entries( dataRef.current ).reduce( ( acc, cur ) => ( {
      ...acc,
      [cur[0]] : typeof cur[1].value === 'string' ? cur[1].value.trim() : cur[1].value
    } ), {} )
    onSubmit( { data : eventData, value } )
    setFormData( initialState )
    onClose( { data : eventData } )
  }

  /**
   * Allow submit by pressing return
   */
  useEffect( () => {
    const submitOnReturn = ( e ) => {
      if ( ( e.which === 13 || e.keyCode === 13 ) && dataRef.current?.type?.value ) {
        handleSubmit()
      }
    }
    if ( show ) {
      document.addEventListener( 'keyup', submitOnReturn )
    } else {
      document.removeEventListener( 'keyup', submitOnReturn )
    }
    return () => document.removeEventListener( 'keyup', submitOnReturn )
  }, [ show ] )

  const onChange = ( { data, value } ) => {
    const nData = { ...formData,
                    [data] : {
                      value,
                      error : ( data !== 'description' && !value ) ? 'Required' : null } }
    setFormData( nData )
  }

  const { type, description, startTime, endTime } = formData
  return (
    <Dialog
      variant="dialogMedium"
      show={show}
      onClose={onClose}
      footer={(
        <Flex variant="dialogButtons">
          <Button onClick={onClose} eventData={eventData} variant="secondary">Cancel</Button>
          <Button
            onClick={handleSubmit}
            eventData={eventData}
            disabled={!type.value}>Create
          </Button>
        </Flex>
      )}
      title="Create New Maintenance Event">
      <div className={S.inputGroup}>
        <Label variant="input">Type (required)</Label>
        <Selector
          eventData="type"
          value={type.value}
          onChange={onChange}
          values={options} />
      </div>
      <div className={S.inputGroup}>
        <Label variant="input">Description</Label>
        <TextInput
          hint="A brief description of what this maintenance event will entail"
          variant="inputGrow"
          eventData="description"
          value={description.value}
          onChange={onChange}
          style={{ width : '100%', maxWidth : '500px' }} />
      </div>
      <div className={S.inputGroup}>
        <Label variant="input">Start Time</Label>
        <DateTimePickerV2
          eventData="startTime"
          value={startTime.value}
          onChange={onChange} />
      </div>
      <div className={S.inputGroup}>
        <Label variant="input">End Time</Label>
        <DateTimePickerV2
          eventData="endTime"
          value={endTime.value}
          onChange={onChange} />
      </div>
    </Dialog>
  )
}

function isAlphaNumeric( keyPressEvent ) {
  const code = keyPressEvent.charCode
  return ( code > 47 && code < 58 ) || // 0-9
    ( code > 64 && code < 91 ) || // A-Z
    ( code > 96 && code < 123 ) // a-z
}
